import React, { useState, useEffect, useContext } from "react";
//  { useState, useRef, useEffect, useContext }
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import Cookies from "js-cookie";

import { Col, Row, OverlayTrigger, Tooltip } from "react-bootstrap";

// Contexts
import { MainContext } from "../../App";

//Custom Hooks components
import FormCheck from "../global/form/FormCheck";
import FormSelect from "../global/form/FormSelect";
import FieldInput from "../global/form/FieldInput";

// services
import { GetClassDetails, GetSubjects, GetTeacherList } from "../../api/class";

// Modals
import ModalAddGrade from "./modals/ModalAddGrade";
import ModalAddGroups from "./modals/ModalAddGroups";
import ModalCreateSubject from "../subjects/modals/ModalCreateSubject";
import ModalCreateStaff from "../../pages/staff/modals/ModalCreateStaff";
import ModalAddAcademicOffering from "./modals/ModalAddAcademicOffering";
import ModalWarningChangeSchooling from "./modals/ModalWarningChangeSchooling";

const PREFIX = process.env.REACT_APP_PREFIX;

const customStyles = {
  iconContainerPlus: {
    alignContent: "center",
    fontSize: "17px",
    paddingTop: "11px",
    color: "var(--red-primary)",
  },
  iconContainerPlusDisabled: {
    alignContent: "center",
    fontSize: "17px",
    paddingTop: "11px",
    color: "var(--red-primary)",
    opacity: 0.5,
    cursor: "not-allowed",
  },
  lazyLabel: {
    height: "17px",
    width: "60%",
  },
};

const ClassDetails = (props) => {
  const [t] = useTranslation(["class", "global"]);
  // Contexts
  const { permissions } = useContext(MainContext);
  const [loading, setLoading] = useState(true);
  const [isHigherLevel, setIsHigherLevel] = useState(false);
  const [cyclesList, setCyclesList] = useState([]);
  const [levelsList, setLevelsList] = useState([]);
  const [gradesList, setGradesList] = useState([]);
  const [groupsList, setGroupsList] = useState([]);
  const [academicOffersList, setAcademicOffersList] = useState([]);
  const [programLevelsList, setProgramLevelsList] = useState([]);

  const [subjectsList, setSubjectsList] = useState([]);
  const [teachersList, setTeachersList] = useState([]);

  // Modals
  const [showModalAddGrade, setShowModalAddGrade] = useState(false);
  const [showModalAddGroup, setShowModalAddGroup] = useState(false);
  const [showModalAddSubject, setShowModalAddSubject] = useState(false);
  const [showModalAddTeacher, setShowModalAddTeacher] = useState(false);
  const [showModalAddAcademicOffering, setShowModalAddAcademicOffering] =
    useState(false);
  const [showWarningChangeSchooling, setShowWarningChangeSchooling] =
    useState(false);
  // Nuevo estado temporal para schoolCycle pendiente
  const [pendingChange, setPendingChange] = useState(null);

  const formik = props.formik;
  const values = formik.values;
  const isNew = props.classId == "new";

  useEffect(() => {
    getCycles();
    getTeachers();
  }, []);

  // funcion para escuchar cambios de Formik despues de renderizar
  useEffect(() => {
    if (!loading && !isNew) {
      props.setClassInformationChange(true);
    }
  }, [values]);

  const formatCycles = (cycles) => {
    return cycles.map((cycle) => ({
      id: cycle.id,
      value: cycle.id,
      label: cycle.school_cycle,
      current: cycle.current,
      levels: cycle.school_levels.map((level) => ({
        id: level.organization_school_level_id,
        school_level_id: level.school_level_id,
        value: level.id,
        label: level.school_level_name,
        grades: level.grades,
        organization_school_level_id: level.organization_school_level_id,
      })),
      higherPrograms:
        cycle.colleges_and_schools.length > 0
          ? obtainHigherLevels(cycle.colleges_and_schools)
          : [],
    }));
  };

  const getClassInfo = (classId) => {
    const payload = {
      class_id: classId,
    };
    GetClassDetails(payload)
      .then((response) => {
        if (response.status) {
          const classInfo = response.data;
          const cycles = JSON.parse(localStorage.getItem(`cycles${PREFIX}`));
          const formattedCycles = formatCycles(cycles);
          // setear filtros para students
          const schoolingFilters = {};
          const cycleSelected = formattedCycles.find(
            (cycle) => cycle.id == classInfo.school_cycle.id,
          );
          setAcademicOffersList(cycleSelected.higherPrograms);
          let subjectsPayload = {};
          let levelSelected = {};
          let gradeSelected = {};
          let groupSelected = {};
          let higherLevel = {};
          let programLevelSelected = {};
          // program_level es solo de nivel superior
          if (classInfo.program_level != null) {
            setIsHigherLevel(true);
            levelSelected = {
              id: "higher_education",
              value: "higher_education",
              label: t("class:classDetails.higherLevel"),
            };
            higherLevel = cycleSelected.higherPrograms.find(
              (program) =>
                program.programSchoolCycleId ==
                classInfo.program_level.program_school_cycle_id,
            );
            setProgramLevelsList(higherLevel.programLevels || []);
            programLevelSelected = higherLevel.programLevels.find(
              (pl) => pl.id == classInfo.program_level.id,
            );
            subjectsPayload = {
              school_cycle_id: cycleSelected.id,
              program_id: higherLevel.id,
            };
            schoolingFilters.program_level_cycle_id = programLevelSelected.id;
          } else {
            setIsHigherLevel(false);
            levelSelected = cycleSelected.levels.find(
              (level) => level.school_level_id == classInfo.school_level.id,
            );
            // get subjects
            subjectsPayload = {
              school_cycle_id: cycleSelected.id,
              organization_school_level_id: levelSelected.id,
            };
            const grades = obtainGradesAndGroups(levelSelected);
            setGradesList(grades || []);
            gradeSelected = grades.find(
              (grade) =>
                grade.id ==
                classInfo.grade_group_grade_levels?.[0]?.grade_level?.id,
            );
            if (
              gradeSelected?.groups?.length > 1 &&
              gradeSelected.groups[0].id !== 0
            ) {
              const allGroupsOption = {
                id: 0,
                value: 0,
                label: t("class:classDetails.allGroups"),
                gggl: null,
              };
              gradeSelected.groups.unshift(allGroupsOption);
            }
            // saber si es multigrupo
            setGroupsList(gradeSelected?.groups || []);
            if (classInfo.grade_group_grade_levels?.length > 1) {
              groupSelected = {
                id: 0,
                value: 0,
                label: t("class:classDetails.allGroups"),
                gggl: null,
              };
            } else {
              groupSelected = gradeSelected?.groups?.find(
                (group) =>
                  group.id ==
                  classInfo.grade_group_grade_levels?.[0]?.grade_group?.id,
              );
            }
            // nivel basico grado y grupo
            if (groupSelected) {
              schoolingFilters.organization_school_level_cycle_id =
                levelSelected.value;
              if (groupSelected.gggl) {
                schoolingFilters.grade_group_grade_level_id =
                  groupSelected.gggl;
              } else {
                schoolingFilters.grade_level_id = gradeSelected.id;
              }
            }
          }
          getSubjects(subjectsPayload);

          const subjectSelected = {
            id: classInfo.subject.id,
            value: classInfo.subject.id,
            label: classInfo.subject.name,
          };

          const teacherSelected = {
            id: classInfo.teacher.id,
            value: classInfo.teacher.id,
            label: `${classInfo.teacher.first_name} ${classInfo.teacher.last_name}`,
            active: classInfo.teacher.active,
          };

          formik.setValues({
            optional: classInfo.optional_class,
            schoolCycle: cycleSelected,
            schoolLevel: levelSelected,

            subject: subjectSelected,
            classTitle: classInfo.class_title,
            classRoom: classInfo.class_room,
            teacher: teacherSelected,

            gradeLevel: gradeSelected,
            gradeGroup: groupSelected,
            academicOffer: higherLevel,
            programLevel: programLevelSelected,
          });
          props.setSchoolingFilters(schoolingFilters);
        } else {
          history.back();
        }
        setTimeout(() => {
          setLoading(false);
        }, 500);
      })
      .catch(() => {});
  };

  const getTeachers = () => {
    const payload = {
      organization_id: Cookies.get(`cmOrganizationID${PREFIX}`),
      limit: 500,
      group_id: 7,
    };
    GetTeacherList(payload)
      .then((response) => {
        if (response.status) {
          let teachers = response.data.map((teacher) => ({
            id: teacher.id,
            value: teacher.id,
            label: teacher.first_name + " " + teacher.last_name,
            active: teacher.active,
          }));
          // filter only active teachers
          teachers = teachers.filter((teacher) => teacher.active == 1);
          setTeachersList(teachers || []);
        }
      })
      .catch(() => {});
  };

  const getCycles = () => {
    const cycles = JSON.parse(localStorage.getItem(`cycles${PREFIX}`));
    const hasCycles = cycles && Array.isArray(cycles) && cycles.length;
    if (hasCycles === null) {
      setTimeout(() => {
        getCycles();
      }, 500);
      return;
    }
    if (hasCycles) {
      const formattedCycles = formatCycles(cycles);

      const cycleSelected =
        formattedCycles.find((cycle) => cycle.current == 1) || cycles[0];

      if (cycleSelected?.higherPrograms.length > 0) {
        cycleSelected.levels.push({
          id: "higher_education",
          value: "higher_education",
          label: t("class:classDetails.higherLevel"),
        });
        setAcademicOffersList(cycleSelected.higherPrograms);
      }

      formik.setFieldValue("schoolCycle", cycleSelected);
      if (cycleSelected.levels.length > 0) {
        const levelSelected = cycleSelected.levels[0];
        // formik.setFieldValue("schoolLevel", levelSelected);
        if (levelSelected && levelSelected.value == "higher_education") {
          setIsHigherLevel(true);
        }

        if (levelSelected.grades) {
          const grades = obtainGradesAndGroups(levelSelected);
          setGradesList(grades || []);
        }
        if (levelSelected && levelSelected.value != "higher_education") {
          const subjectsPayload = {
            school_cycle_id: cycleSelected.id,
            organization_school_level_id: levelSelected.id,
          };
          getSubjects(subjectsPayload);
        }
      }

      setCyclesList(formattedCycles);
      setLevelsList(cycleSelected.levels);
    }
    if (props.classId == "new") {
      setTimeout(() => {
        setLoading(false);
      }, 500);
    } else {
      getClassInfo(props.classId);
    }
  };

  const getSubjects = (data = false) => {
    let payload = {};
    if (!data) {
      payload = {
        school_cycle_id: values.schoolCycle.id,
        organization_school_level_id: values.schoolLevel.id,
      };
    } else {
      payload = data;
    }
    GetSubjects(payload)
      .then((response) => {
        if (response.status) {
          const subjects = response.data.map((subject) => ({
            id: subject.id,
            value: subject.id,
            label: subject.name,
          }));
          setSubjectsList(subjects || []);
        }
      })
      .catch(() => {});
  };

  const obtainHigherLevels = (colleges_and_schools) => {
    // extraer los niveles educativos de las facultades y escuelas
    let higherPrograms = [];
    colleges_and_schools.forEach((college) => {
      if (college.school_levels && college.school_levels.length > 0) {
        college.school_levels.forEach((level) => {
          level.programs.forEach((program) => {
            if (program.enabled && program.program_levels.length > 0) {
              higherPrograms.push({
                id: program.id,
                value: program.id,
                label: program.name + " (" + level.school_level_name + ")",
                programLevels: program.program_levels.map((programLevel) => ({
                  id: programLevel.id,
                  programLevelId: programLevel.program_level_id,
                  programSchoolCycleId: programLevel.program_school_cycle_id,
                  value: programLevel.id,
                  label: programLevel.level,
                })),
                programLevelId: program.program_levels[0]?.program_level_id,
                programSchoolCycleId:
                  program.program_levels[0]?.program_school_cycle_id,
              });
            }
          });
        });
      }
    });

    // eliminar duplicados
    const uniqueLevels = Array.from(
      new Set(higherPrograms.map((level) => level.id)),
    ).map((id) => {
      return higherPrograms.find((level) => level.id === id);
    });

    return uniqueLevels;
  };

  const obtainGradesAndGroups = (level) => {
    let grades = level.grades.map((grade) => ({
      id: grade.id,
      value: grade.id,
      label: grade.grade_level,
      groups: grade.groups.map((group) => ({
        id: group.id,
        value: group.id,
        label: group.name,
        gggl: group.grade_group_grade_level_id,
      })),
    }));
    return grades;
  };

  const lazyLoadingForm = () => {
    return (
      <div>
        <Row className="table-lazyLoading mb-3">
          <Col md={6}>
            <div style={customStyles.lazyLabel}></div>
          </Col>
        </Row>
        {[1, 2, 3, 4].map((e) => {
          return (
            <Row key={e} className="table-lazyLoading mb-3">
              <Col md={6}>
                <div style={customStyles.lazyLabel}></div>
                <div style={customStyles.lazy}></div>
              </Col>
              <Col md={6}>
                <div style={customStyles.lazyLabel}></div>
                <div style={customStyles.lazy}></div>
              </Col>
            </Row>
          );
        })}
      </div>
    );
  };

  // Ahora schoolingChangeMonitor recibe el ciclo pendiente
  const schoolingChangeMonitor = (event, field) => {
    if (isNew) return;
    const change = {
      event: event,
      field: field,
    };
    setPendingChange(change);
    if (!props.changeSchooling) {
      setShowWarningChangeSchooling(true);
    } else {
      changeSchoolingConfirmed(change);
    }
  };

  // Confirmar el cambio de ciclo escolar
  const changeSchoolingConfirmed = (change = pendingChange) => {
    if (change) {
      switch (change.field) {
        case "schoolCycle":
          changeCycle(change.event);
          break;
        case "schoolLevel":
          changeLevel(change.event);
          break;
        case "gradeLevel":
          changeGrade(change.event);
          break;
        case "gradeGroup":
          changeGroup(change.event);
          break;
        case "academicOffer":
          changeAcademicOffer(change.event);
          break;
        case "programLevel":
          changeProgramLevel(change.event);
          break;
        default:
          break;
      }
    }
    setShowWarningChangeSchooling(false);
    props.setChangeSchooling && props.setChangeSchooling(true);
  };

  const changeCycle = (event = pendingChange.event) => {
    if (
      event.higherPrograms.length > 0 &&
      !event.levels.find((l) => l.value === "higher_education")
    ) {
      event.levels.push({
        id: "higher_education",
        value: "higher_education",
        label: t("class:classDetails.higherLevel"),
      });
    }
    formik.setFieldValue("schoolCycle", event);
    setLevelsList(event.levels || []);
    setIsHigherLevel(false);
    formik.setFieldValue("schoolLevel", "");
    formik.setFieldValue("gradeLevel", "");
    formik.setFieldValue("gradeGroup", "");
    formik.setFieldValue("subject", "");
    formik.setFieldValue("academicOffer", "");
    formik.setFieldValue("programLevel", "");
    setGradesList([]);
    setGroupsList([]);
    setAcademicOffersList([]);
    setProgramLevelsList([]);
    props.setSchoolingFilters("clean");
    setPendingChange(null);
  };

  const changeLevel = (event = pendingChange.event) => {
    formik.setFieldValue("schoolLevel", event);
    formik.setFieldValue("gradeLevel", "");
    formik.setFieldValue("gradeGroup", "");
    formik.setFieldValue("subject", "");
    formik.setFieldValue("academicOffer", "");
    formik.setFieldValue("programLevel", "");
    setIsHigherLevel(event.value === "higher_education");
    let grades = [];
    if (event.value == "higher_education") {
      setIsHigherLevel(true);
      setAcademicOffersList(values.schoolCycle.higherPrograms);
      setSubjectsList([]);
    } else {
      grades = obtainGradesAndGroups(event);
      const subjectsPayload = {
        school_cycle_id: values.schoolCycle.id,
        organization_school_level_id: event.id,
      };
      getSubjects(subjectsPayload);
    }
    setGradesList(grades);
    setGroupsList([]);
    props.setSchoolingFilters("clean");
  };

  const changeGrade = (event = pendingChange.event) => {
    formik.setFieldValue("gradeLevel", event);
    formik.setFieldValue("gradeGroup", "");
    // si hay grupos, añadir la opcion de todos los grupos
    if (event?.groups && event.groups.length > 1 && event.groups[0].id !== 0) {
      const allGroupsOption = {
        id: 0,
        value: 0,
        label: t("class:classDetails.allGroups"),
        gggl: null,
      };
      event.groups.unshift(allGroupsOption);
    }
    setGroupsList(event?.groups || []);
    props.setSchoolingFilters("clean");
  };

  const changeGroup = (event = pendingChange.event) => {
    formik.setFieldValue("gradeGroup", event);

    if (event.value === 0) {
      // all groups selected
      const payload = {
        organization_school_level_cycle_id: values.schoolLevel.value,
        grade_level_id: values.gradeLevel.id,
      };
      props.setSchoolingFilters(payload);
      return;
    }

    const payload = {
      organization_school_level_cycle_id: values.schoolLevel.value,
      grade_group_grade_level_id: event.gggl,
    };
    props.setSchoolingFilters(payload);
  };

  const changeAcademicOffer = (event = pendingChange.event) => {
    formik.setFieldValue("academicOffer", event);
    setProgramLevelsList(event.programLevels || []);
    const subjectsPayload = {
      school_cycle_id: values.schoolCycle.id,
      program_id: event.id,
    };
    getSubjects(subjectsPayload);
    props.setSchoolingFilters("clean");
  };

  const changeProgramLevel = (event = pendingChange.event) => {
    formik.setFieldValue("programLevel", event);
    const payload = {
      program_level_cycle_id: event.id,
    };
    props.setSchoolingFilters(payload);
  };

  return (
    <>
      <Row>
        <Col md={12} className="mb-2">
          <h5>
            <b>{t("class:classDetails.title")}</b>
          </h5>
        </Col>
      </Row>
      {loading && lazyLoadingForm()}
      {!loading && (
        <>
          {/* optional class */}
          <Row>
            <Col md={12}>
              <FormCheck
                id="optionalClass"
                name="optional"
                label={t("class:classDetails.classIsOptional")}
                type="checkbox"
                disabled={!permissions.classes.create}
              />
            </Col>
          </Row>
          {/* schoolCycle Escolaridad */}
          <Row>
            <Col md={6}>
              <Row>
                <Col xs={11} sm={11} md={11} lg={11}>
                  <FormSelect
                    id="schoolCycle"
                    label={t("class:classDetails.schoolCycle")}
                    name="schoolCycle"
                    options={cyclesList}
                    onChange={(event) => {
                      if (isNew) {
                        changeCycle(event);
                        return;
                      }
                      // Guardar el ciclo pendiente y mostrar modal de confirmación
                      schoolingChangeMonitor(event, "schoolCycle");
                    }}
                    value={values.schoolCycle}
                    isDisabled={!permissions.classes.create}
                  />
                </Col>
                <Col
                  xs={1}
                  sm={1}
                  md={1}
                  lg={1}
                  className={
                    !permissions.classes.create
                      ? "px-0 icon-btn-disabled"
                      : "px-0"
                  }
                  style={
                    !permissions.classes.create
                      ? customStyles.iconContainerPlusDisabled
                      : customStyles.iconContainerPlus
                  }
                >
                  <OverlayTrigger
                    placement="top"
                    overlay={
                      !permissions.classes.create ? (
                        <span />
                      ) : (
                        <Tooltip id="tooltip-group">
                          {t("global:buttons.add")}
                        </Tooltip>
                      )
                    }
                  >
                    <i
                      className="bi bi-plus-circle-fill"
                      id="addCycle"
                      role="button"
                      onClick={() => {
                        if (!permissions.classes.create) return;
                        window.open(
                          `${process.env.REACT_APP_V1}/configuraciones/ciclo`,
                          "_blank",
                        );
                      }}
                    ></i>
                  </OverlayTrigger>
                </Col>
              </Row>
            </Col>
            <Col md={6}>
              <Row>
                <Col xs={11} sm={11} md={11} lg={11}>
                  <FormSelect
                    id="schoolLevel"
                    label={t("class:classDetails.schoolLevel")}
                    name="schoolLevel"
                    options={levelsList}
                    onChange={(event) => {
                      if (isNew) {
                        changeLevel(event);
                        return;
                      }
                      // Guardar el nivel pendiente y mostrar modal de confirmación
                      schoolingChangeMonitor(event, "schoolLevel");
                    }}
                    value={values.schoolLevel}
                    isDisabled={!permissions.classes.create}
                  />
                </Col>
                <Col
                  xs={1}
                  sm={1}
                  md={1}
                  lg={1}
                  className="px-0"
                  style={customStyles.iconContainerPlus}
                ></Col>
              </Row>
            </Col>
          </Row>
          {isHigherLevel ? (
            /* Oferta academica  */
            <Row>
              <Col md={6}>
                <Row>
                  <Col xs={11} sm={11} md={11} lg={11}>
                    <FormSelect
                      id="academicOffer"
                      label={t("class:classDetails.academicOffering")}
                      name="academicOffer"
                      options={academicOffersList}
                      onChange={(event) => {
                        if (isNew) {
                          changeAcademicOffer(event);
                          return;
                        }
                        schoolingChangeMonitor(event, "academicOffer");
                      }}
                      value={values.academicOffer}
                      isDisabled={!permissions.classes.create}
                    />
                  </Col>
                  <Col
                    xs={1}
                    sm={1}
                    md={1}
                    lg={1}
                    className="px-0"
                    style={customStyles.iconContainerPlus}
                  >
                    <OverlayTrigger
                      placement="top"
                      overlay={
                        <Tooltip id="tooltip-group">
                          {t("global:buttons.add")}
                        </Tooltip>
                      }
                    >
                      <i
                        className="hide bi bi-plus-circle-fill"
                        id="addAcademicOffert"
                        role="button"
                        onClick={() => setShowModalAddAcademicOffering(true)}
                      ></i>
                    </OverlayTrigger>
                  </Col>
                </Row>
              </Col>

              <Col md={6} hidden={!isHigherLevel}>
                <Row>
                  <Col xs={11} sm={11} md={11} lg={11}>
                    <FormSelect
                      id="subject"
                      label={t("class:classDetails.subject")}
                      name="subject"
                      options={subjectsList}
                      onChange={(event) => {
                        formik.setFieldValue("subject", event);
                      }}
                      value={values.subject}
                      isDisabled={!permissions.classes.create}
                    />
                  </Col>
                  <Col
                    xs={1}
                    sm={1}
                    md={1}
                    lg={1}
                    className={
                      !permissions.classes.create ||
                      values.academicOffer == "" ||
                      !values.academicOffer
                        ? "px-0 icon-btn-disabled"
                        : "px-0"
                    }
                    style={
                      !permissions.classes.create ||
                      values.academicOffer == "" ||
                      !values.academicOffer
                        ? customStyles.iconContainerPlusDisabled
                        : customStyles.iconContainerPlus
                    }
                  >
                    <OverlayTrigger
                      placement="top"
                      overlay={
                        !permissions.classes.create ? (
                          <span />
                        ) : (
                          <Tooltip id="tooltip-group">
                            {t("global:buttons.add")}
                          </Tooltip>
                        )
                      }
                    >
                      <i
                        className="bi bi-plus-circle-fill"
                        id="addSubject"
                        role="button"
                        onClick={() => {
                          if (
                            !permissions.classes.create ||
                            values.academicOffer == "" ||
                            !values.academicOffer
                          ) {
                            return;
                          }
                          setShowModalAddSubject(true);
                        }}
                      ></i>
                    </OverlayTrigger>
                  </Col>
                </Row>
              </Col>
            </Row>
          ) : (
            /* Grado Grupo */
            <Row>
              {/* Grado */}
              <Col md={6}>
                <Row>
                  <Col xs={11} sm={11} md={11} lg={11}>
                    <FormSelect
                      id="grade"
                      label={t("class:classDetails.grade")}
                      name="gradeLevel"
                      options={gradesList}
                      onChange={(event) => {
                        if (isNew) {
                          changeGrade(event);
                          return;
                        }
                        // Guardar el grade pendiente y mostrar modal de confirmación
                        schoolingChangeMonitor(event, "gradeLevel");
                      }}
                      value={values.gradeLevel}
                      isDisabled={
                        !permissions.classes.create ||
                        values.schoolLevel == "" ||
                        !values.schoolLevel
                      }
                    />
                  </Col>
                  <Col
                    xs={1}
                    sm={1}
                    md={1}
                    lg={1}
                    className={
                      !permissions.classes.create
                        ? "px-0 icon-btn-disabled"
                        : "px-0"
                    }
                    style={
                      !permissions.classes.create
                        ? customStyles.iconContainerPlusDisabled
                        : customStyles.iconContainerPlus
                    }
                  >
                    <OverlayTrigger
                      placement="top"
                      overlay={
                        !permissions.classes.create ? (
                          <span />
                        ) : (
                          <Tooltip id="tooltip-group">
                            {t("global:buttons.add")}
                          </Tooltip>
                        )
                      }
                    >
                      <i
                        className="bi bi-plus-circle-fill"
                        id="addGrade"
                        role="button"
                        onClick={() => {
                          if (!permissions.classes.create) return;
                          setShowModalAddGrade(true);
                        }}
                      ></i>
                    </OverlayTrigger>
                  </Col>
                </Row>
              </Col>
              {/* Grupo */}
              <Col md={6}>
                <Row>
                  <Col xs={11} sm={11} md={11} lg={11}>
                    <FormSelect
                      id="gradeGroup"
                      label={t("class:classDetails.group")}
                      name="gradeGroup"
                      options={groupsList}
                      onChange={(event) => {
                        if (isNew) {
                          changeGroup(event);
                          return;
                        }
                        // Guardar el grupo pendiente y mostrar modal de confirmación
                        schoolingChangeMonitor(event, "gradeGroup");
                      }}
                      value={values.gradeGroup}
                      isDisabled={
                        !permissions.classes.create ||
                        values.gradeLevel == "" ||
                        !values.gradeLevel
                      }
                    />
                  </Col>
                  <Col
                    xs={1}
                    sm={1}
                    md={1}
                    lg={1}
                    className={
                      !permissions.classes.create ||
                      values.gradeLevel == "" ||
                      !values.gradeLevel
                        ? "px-0 icon-btn-disabled"
                        : "px-0"
                    }
                    style={
                      !permissions.classes.create ||
                      values.gradeLevel == "" ||
                      !values.gradeLevel
                        ? customStyles.iconContainerPlusDisabled
                        : customStyles.iconContainerPlus
                    }
                  >
                    <OverlayTrigger
                      placement="top"
                      overlay={
                        !permissions.classes.create ? (
                          <span />
                        ) : (
                          <Tooltip id="tooltip-group">
                            {t("global:buttons.add")}
                          </Tooltip>
                        )
                      }
                    >
                      <i
                        className="bi bi-plus-circle-fill"
                        id="addGroup"
                        role="button"
                        onClick={() => {
                          if (
                            !permissions.classes.create ||
                            values.gradeLevel == "" ||
                            !values.gradeLevel
                          ) {
                            return;
                          }
                          setShowModalAddGroup(true);
                        }}
                      ></i>
                    </OverlayTrigger>
                  </Col>
                </Row>
              </Col>
            </Row>
          )}
          {/* subject classTitle */}
          <Row>
            <Col md={6} hidden={isHigherLevel}>
              <Row>
                <Col xs={11} sm={11} md={11} lg={11}>
                  <FormSelect
                    id="subject"
                    label={t("class:classDetails.subject")}
                    name="subject"
                    options={subjectsList}
                    onChange={(event) => {
                      formik.setFieldValue("subject", event);
                    }}
                    value={values.subject}
                    isDisabled={!permissions.classes.create}
                    //   showRequired={isRequired}
                  />
                </Col>
                <Col
                  xs={1}
                  sm={1}
                  md={1}
                  lg={1}
                  className={
                    !permissions.classes.create ||
                    values.schoolLevel == "" ||
                    !values.schoolLevel
                      ? "px-0 icon-btn-disabled"
                      : "px-0"
                  }
                  style={
                    !permissions.classes.create ||
                    values.schoolLevel == "" ||
                    !values.schoolLevel
                      ? customStyles.iconContainerPlusDisabled
                      : customStyles.iconContainerPlus
                  }
                >
                  <OverlayTrigger
                    placement="top"
                    overlay={
                      !permissions.classes.create ? (
                        <span />
                      ) : (
                        <Tooltip id="tooltip-group">
                          {t("global:buttons.add")}
                        </Tooltip>
                      )
                    }
                  >
                    <i
                      className="bi bi-plus-circle-fill"
                      id="addSubject"
                      role="button"
                      onClick={() => {
                        if (
                          !permissions.classes.create ||
                          values.schoolLevel == "" ||
                          !values.schoolLevel
                        ) {
                          return;
                        }
                        setShowModalAddSubject(true);
                      }}
                    ></i>
                  </OverlayTrigger>
                </Col>
              </Row>
            </Col>
            <Col md={isHigherLevel ? 12 : 6}>
              <Row>
                <Col
                  xs={11}
                  sm={11}
                  md={11}
                  lg={11}
                  className={isHigherLevel ? "pe-0" : ""}
                >
                  <FieldInput
                    id="classTitle"
                    label={t("class:classDetails.classTitle")}
                    // capitalize={true}
                    name="classTitle"
                    type="text"
                    placeholder={t("class:classDetails.fieldOptional")}
                    disabled={!permissions.classes.create}
                    //   showRequired={isRequired}
                  />
                </Col>
                <Col
                  xs={1}
                  sm={1}
                  md={1}
                  lg={1}
                  className="px-0"
                  style={customStyles.iconContainerPlus}
                ></Col>
              </Row>
            </Col>
          </Row>
          {/* classRoom teacher */}
          <Row>
            <Col md={6}>
              <Row>
                <Col xs={11} sm={11} md={11} lg={11}>
                  <FieldInput
                    id="classRoom"
                    label={t("class:classDetails.assignedSpace")}
                    capitalize={true}
                    name="classRoom"
                    type="text"
                    placeholder={t("class:classDetails.fieldOptional")}
                    disabled={!permissions.classes.create}
                    //   showRequired={isRequired}
                  />
                </Col>
                <Col
                  xs={1}
                  sm={1}
                  md={1}
                  lg={1}
                  className="px-0"
                  style={customStyles.iconContainerPlus}
                ></Col>
              </Row>
            </Col>
            <Col md={6}>
              <Row>
                <Col xs={11} sm={11} md={11} lg={11}>
                  <FormSelect
                    id="teacher"
                    label={t("class:classDetails.assignedTeacher")}
                    name="teacher"
                    options={teachersList}
                    onChange={(event) => {
                      formik.setFieldValue("teacher", event);
                    }}
                    value={values.teacher}
                    isDisabled={!permissions.classes.create}
                  />
                </Col>
                <Col
                  xs={1}
                  sm={1}
                  md={1}
                  lg={1}
                  className={
                    !permissions.classes.create
                      ? "px-0 icon-btn-disabled"
                      : "px-0"
                  }
                  style={
                    !permissions.classes.create
                      ? customStyles.iconContainerPlusDisabled
                      : customStyles.iconContainerPlus
                  }
                >
                  <OverlayTrigger
                    placement="top"
                    overlay={
                      !permissions.classes.create ? (
                        <span />
                      ) : (
                        <Tooltip id="tooltip-group">
                          {t("global:buttons.add")}
                        </Tooltip>
                      )
                    }
                  >
                    <i
                      className="bi bi-plus-circle-fill"
                      id="addTeacher"
                      role="button"
                      onClick={() => {
                        if (!permissions.classes.create) return;
                        setShowModalAddTeacher(true);
                      }}
                    ></i>
                  </OverlayTrigger>
                </Col>
              </Row>
            </Col>
          </Row>

          {/* program_level */}
          <Row>
            <Col md={6} hidden={!isHigherLevel}>
              <Row>
                <Col xs={11} sm={11} md={11} lg={11}>
                  <FormSelect
                    id="programLevel"
                    label={t("class:classDetails.programLevel")}
                    name="programLevel"
                    options={programLevelsList}
                    onChange={(event) => {
                      if (isNew) {
                        changeProgramLevel(event);
                        return;
                      }
                      schoolingChangeMonitor(event, "programLevel");
                    }}
                    value={values.programLevel}
                    isDisabled={
                      !permissions.classes.create ||
                      values.academicOffer == "" ||
                      !values.academicOffer
                    }
                  />
                </Col>
                <Col
                  xs={1}
                  sm={1}
                  md={1}
                  lg={1}
                  className="px-0"
                  style={customStyles.iconContainerPlus}
                ></Col>
              </Row>
            </Col>
          </Row>
        </>
      )}

      {/* Modals */}
      {showModalAddGrade && (
        <ModalAddGrade
          showModalAddGrade={showModalAddGrade}
          setShowModalAddGrade={setShowModalAddGrade}
          getGrades={() => {}}
          data={{
            schoolCycleId: values.schoolCycle.id,
            organizationSchoolLevelId: values.schoolLevel.id,
          }}
          setGradesList={setGradesList}
        />
      )}
      {showModalAddGroup && (
        <ModalAddGroups
          showModalAddGroup={showModalAddGroup}
          setShowModalAddGroup={setShowModalAddGroup}
          gradeSelected={{
            id: values.gradeLevel.id,
            value: values.gradeLevel.id,
            label: values.gradeLevel.label,
            groups: values.gradeLevel.groups.filter((g) => g.id !== 0),
          }}
          data={{
            schoolCycleId: values.schoolCycle.id,
            organizationSchoolLevelId: values.schoolLevel.id,
          }}
          setGradesList={setGradesList}
          setGroupsList={setGroupsList}
        />
      )}
      {showModalAddSubject && (
        <ModalCreateSubject
          showCreateSubject={showModalAddSubject}
          setShowCreateSubject={setShowModalAddSubject}
          schoolCycle={values.schoolCycle}
          schoolLevel={{
            id:
              values.schoolLevel.id == "higher_education"
                ? "higherLevel"
                : values.schoolLevel.id,
            value: values.schoolLevel.id,
          }}
          academicOffert={values.academicOffer}
          refreshList={() => {
            const subjectsPayload = {
              school_cycle_id: values.schoolCycle.id,
              organization_school_level_id: values.schoolLevel.id,
            };
            getSubjects(subjectsPayload);
          }}
        />
      )}
      {showModalAddTeacher && (
        <ModalCreateStaff
          showModalStaff={showModalAddTeacher}
          setShowModalStaff={setShowModalAddTeacher}
          listStaff={() => {
            getTeachers();
          }}
          specificRole={{
            id: 7,
            label: "Maestro",
            name: "Maestro",
            nameId: "teacher",
            value: 7,
          }}
        />
      )}
      {showModalAddAcademicOffering && (
        <ModalAddAcademicOffering
          showModalAddAcademicOffering={showModalAddAcademicOffering}
          setShowModalAddAcademicOffering={setShowModalAddAcademicOffering}
          collegesAndSchools={values.schoolCycle.higherPrograms}
          refreshList={() => {
            const updatedCycles = [...cyclesList];
            const cycleIndex = updatedCycles.findIndex(
              (cycle) => cycle.id === values.schoolCycle.id,
            );
            if (cycleIndex !== -1) {
              const cycle = updatedCycles[cycleIndex];
              const higherPrograms = obtainHigherLevels(
                cycle.colleges_and_schools,
              );
              cycle.higherPrograms = higherPrograms;
              updatedCycles[cycleIndex] = cycle;
              setCyclesList(updatedCycles);
              formik.setFieldValue("schoolCycle", cycle);
              setAcademicOffersList(higherPrograms);
            }
          }}
        />
      )}
      {showWarningChangeSchooling && (
        <ModalWarningChangeSchooling
          show={showWarningChangeSchooling}
          onClose={() => {
            setShowWarningChangeSchooling(false);
            setPendingChange(null);
          }}
          changeSchoolingConfirmed={changeSchoolingConfirmed}
        />
      )}
    </>
  );
};

ClassDetails.propTypes = {
  mainState: PropTypes.object,
  formik: PropTypes.object.isRequired,
  setValidateSchema: PropTypes.func.isRequired,
  classId: PropTypes.string,
  setSchoolingFilters: PropTypes.func,
  setChangeSchooling: PropTypes.func,
  changeSchooling: PropTypes.bool,
  setClassInformationChange: PropTypes.func,
};
export default ClassDetails;
